import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

interface Auth {
	isAuthenticated: boolean;
	email: string;
}

let reactPlugin = new ReactPlugin();
let appInsights: ApplicationInsights | null = null;

interface TelemetryService {
	reactPlugin: ReactPlugin;
	appInsights: ApplicationInsights | null;
	initialize: (instrumentationKey: string) => void;
	updateData: (auth: Auth) => void;
}

/**
 * Create the App Insights Telemetry Service
 * @return {{reactPlugin: ReactPlugin, appInsights: Object, initialize: Function}} - Object
 */
const createTelemetryService = (): TelemetryService => {
	/**
	 * Initialize the Application Insights instance
	 * @param {string} instrumentationKey - Application Insights Instrumentation Key
	 * @return {void}
	 */
	const initialize = (instrumentationKey: string): void => {
		if (!instrumentationKey) {
			throw new Error('Instrumentation key not provided.');
		}

		appInsights = new ApplicationInsights({
			config: {
				autoTrackPageVisitTime: true,
				connectionString: instrumentationKey,
				disableFetchTracking: false,
				disableTelemetry: process.env.NODE_ENV !== 'production',
				enableAutoRouteTracking: true,
				enableCorsCorrelation: true,
				enableRequestHeaderTracking: true,
				enableUnhandledPromiseRejectionTracking: true,
				extensions: [reactPlugin as any],
				extensionConfig: {
					[reactPlugin.identifier]: {},
				},
				loggingLevelTelemetry: 2,
				maxBatchInterval: 0,
				correlationHeaderExcludedDomains: ['*.alida.com'],
			},
		});

		appInsights.loadAppInsights();
	};

	/**
	 * Update telemetry data with authenticated user information
	 * @param {Auth} auth - Authentication details of the user
	 * @return {void}
	 */
	const updateData = (auth: { isAuthenticated: any; email: any }) => {
		if (!appInsights) {
			console.warn('App Insights not initialized. Telemetry not updated.');
			return;
		}

		// context.user can briefly be null before loadAppInsights() finishes; guard it.
		if (!appInsights.context || !appInsights.context.user) {
			return;
		}

		if (auth && auth.isAuthenticated && auth.email) {
			// Sets ai.user.authUserId so the portal's Authenticated User analytics work,
			// rather than stamping a custom field that those views can't read.
			appInsights.context.user.setAuthenticatedUserContext(auth.email);
		} else {
			appInsights.context.user.clearAuthenticatedUserContext();
		}
	};

	// `appInsights` is reassigned in initialize(); expose it as a getter so the
	// service always returns the live instance rather than the null snapshot.
	return {
		reactPlugin,
		get appInsights() {
			return appInsights;
		},
		initialize,
		updateData,
	};
};

export const ai = createTelemetryService();
export const getAppInsights = (): ApplicationInsights | null => appInsights;
